import React, { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppLayout from "layouts/app-layout";
import AuthLayout from "layouts/auth-layout";
import { APP_PREFIX_PATH, AUTH_PREFIX_PATH } from "configs/AppConfig";
import { isEmpty, isLoaded, useFirestore } from "react-redux-firebase";
import * as dayjs from "dayjs";
import { FIRESTORE_USERS_TABLE } from "../constants/FirestoreConstant";
import { useSelector } from "react-redux";
import Loading from "components/shared-components/Loading";
import { Button, Result } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../data/hooks/useAuth";
import { report } from "../functions/api/guardian";
import {
  getCmsSessionState,
  getNoAccessReason,
  SESSION_ERROR,
  SESSION_LOADING,
  SESSION_NO_ACCESS,
  SESSION_OK,
} from "../functions/cmsSession";

// A wrapper that routes user to the dashboard when he/she lands on
// the login page while already authenticated
const CheckAlreadyAuthenticated = ({ children, auth }) => {
  return isEmpty(auth) || !auth.emailVerified ? children : <Navigate to={APP_PREFIX_PATH} replace />;
};

// A wrapper for <Route> that redirects to the login screen if you're not
// authenticated. Zolang Firebase de sessie nog herstelt (dat kost bij elke
// pagina-lading een netwerkronde) een laadscherm, niet de loginpagina: op een
// trage verbinding stond die anders seconden in beeld bij een geldige sessie,
// en ging de pagina waar je heen wilde verloren.
export const PrivateRoute = ({ children, auth }) => {
  if (!isLoaded(auth)) return <Loading cover="page" />;
  return !isEmpty(auth) ? children : <Navigate to={AUTH_PREFIX_PATH} replace />;
};

// Ingelogd is niet genoeg: het profiel moet een CMS-rol hebben (`role` of
// `cmsRole`, zie functions/permissions.js). Een webshopklant die op de een of
// andere manier een sessie heeft, wordt uitgelogd en terug naar de login
// gestuurd in plaats van een leeg CMS te zien.
// Maar alleen als het profiel ook echt gelezen is: kon het niet worden geladen
// (fout op de listener), dan blijft de sessie staan en probeert useAuth het
// opnieuw. Zie functions/cmsSession.js.
export const RequireCmsRole = ({ children }) => {
  const { t } = useTranslation();
  const { profile, profileError, profileLoading, loading, signOut, retryProfile, uid } = useAuth();
  const firestore = useFirestore();
  const state = getCmsSessionState({ loading, profileLoading, profile, profileError });
  const ready = state === SESSION_OK;

  // Elke uitlog door het CMS zelf naar Guardian, één keer per sessie: dit
  // was onzichtbaar, waardoor "ik werd uitgelogd" niet te herleiden was.
  const endedForUid = useRef(null);
  useEffect(() => {
    if (state !== SESSION_NO_ACCESS || !uid || endedForUid.current === uid) return;
    endedForUid.current = uid;
    const reason = getNoAccessReason(profile);
    report({
      // Een ingetrokken CMS-rol is bewust; een ontbrekend profieldocument niet.
      severity: reason === "no_profile_document" ? "error" : "info",
      type: "cms_session_ended",
      title: "CMS-sessie beëindigd door het CMS",
      context: { reason },
    });
    signOut();
  }, [state, uid, profile, signOut]);

  // `lastLoginTimestamp` op het profiel: één keer per keer dat het CMS laadt
  // met een ingelogde CMS-gebruiker — dus bij inloggen én bij een refresh van
  // de browser, niet bij elke navigatie. Zelfde veldnaam als de webshop bij
  // klanten gebruikt. De regels staan een self-update toe zolang de
  // beschermde velden (rol, landen, ...) niet mee veranderen.
  const stampedForUid = useRef(null);
  useEffect(() => {
    if (!ready || !uid || stampedForUid.current === uid) return;
    stampedForUid.current = uid;
    firestore
      .collection(FIRESTORE_USERS_TABLE)
      .doc(uid)
      .update({ lastLoginTimestamp: dayjs().unix() })
      .catch((err) => console.error("Could not update lastLoginTimestamp", err));
  }, [ready, uid, firestore]);
  if (state === SESSION_LOADING) return <Loading cover="page" />;
  if (state === SESSION_ERROR)
    return (
      <Result
        status="warning"
        title={t("session_guard.profile_error_title")}
        subTitle={t("session_guard.profile_error_description")}
        extra={[
          <Button type="primary" key="retry" onClick={retryProfile}>
            {t("session_guard.retry")}
          </Button>,
          <Button key="logout" onClick={signOut}>
            {t("logout")}
          </Button>,
        ]}
      />
    );
  if (state === SESSION_NO_ACCESS) return <Navigate to={AUTH_PREFIX_PATH} replace />;
  return children;
};

export const Views = () => {
  const location = useLocation();
  const auth = useSelector((state) => state.firebaseReducer.auth);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <>
            <Navigate to={APP_PREFIX_PATH} replace />
          </>
        }
      />
      <Route
        path={AUTH_PREFIX_PATH + "/*"}
        element={
          <>
            <CheckAlreadyAuthenticated auth={auth}>
              <AuthLayout />
            </CheckAlreadyAuthenticated>
          </>
        }
      />
      <Route
        path={APP_PREFIX_PATH + "/*"}
        element={
          <>
            <PrivateRoute auth={auth}>
              <RequireCmsRole>
                <AppLayout location={location} />
              </RequireCmsRole>
            </PrivateRoute>
          </>
        }
      />
    </Routes>
  );
};

export default Views;
